/* eslint-disable no-console */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tenantApiPath } from "../../actions/tenantApi";
import { convert_to_comma_seperated_list } from "../../actions/util";
import { callAvosApi } from "../../utils/useAvosApiHook";

interface PatchLayerPayload {
  field: string;
  state_value: any;
  value: any;
  keep_history: boolean;
}

export const layerQueryKey = (layer: any) => ["layer", layer?.tenant_id, layer?.id];

// format2Frontend flattens meta rows onto the top level and derives location_id/
// stage/facility from latest_location, so an optimistic write has to keep both
// representations in step or the next format pass reinstates the old value.
export function applyLayerField(current: any, field: string, value: any) {
    if (!current) {
        return current;
    }

    const meta = Array.isArray(current.meta)
        ? current.meta.map((item: any) => (item?.key === field ? { ...item, value } : item))
        : current.meta;

    const patched = { ...current, [field]: value, meta };

    // The overview renders these two off latest_location rather than the top level.
    if (field === "stage" || field === "location_id") {
        patched.latest_location = {
            ...(current.latest_location && typeof current.latest_location === "object" ? current.latest_location : {}),
            [field]: value,
        };
    }

    return patched;
}

// Keeps rows already on screen in the overview in step with a patch from that table.
function applyLayerFieldToList(current: any, layerId: string, field: string, value: any) {
    if (!current?.results) {
        return current;
    }
    return {
        ...current,
        results: current.results.map((i: any) => (i.id === layerId ? applyLayerField(i, field, value) : i)),
    };
}

function writeLayerFieldToCache(queryClient: any, layer: any, field: string, value: any) {
    queryClient.setQueryData(layerQueryKey(layer), (current: any) => applyLayerField(current, field, value));
    queryClient.setQueriesData(
        { queryKey: ["layers", layer?.tenant_id] },
        (current: any) => applyLayerFieldToList(current, layer?.id, field, value)
    );
}

// For callers that want the value on screen now and will write it later (debounced forms).
export function useApplyLayerFieldToCache() {
    const queryClient = useQueryClient();
    return (layer: any, field: string, value: any) => writeLayerFieldToCache(queryClient, layer, field, value);
}

/**
 * Mutation hook for patching a layer field.
 * Derives tenant_id and layer_id from the layer entity.
 *
 * Usage:
 *   const mutation = usePatchLayerMutation();
 *   mutation.mutate({layer, field: "label", value: "New Label"});
 */
export function usePatchLayerMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        onMutate: async ({ layer, field, value }) => {
            const queryKey = layerQueryKey(layer);
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData(queryKey);
            writeLayerFieldToCache(queryClient, layer, field, value);
            return { queryKey, previous };
        },
        mutationFn: async (args: {
      layer: any;
      field: string;
      value: any;
      keep_history?: boolean;
    }) => {
            const { layer, field, value, keep_history = false } = args;

            if (!layer?.tenant_id || !layer?.id) {
                throw new Error("Cannot patch a layer without tenant_id and id");
            }

            const payload: PatchLayerPayload = {
                field,
                state_value: value,
                value: convert_to_comma_seperated_list(value),
                keep_history,
            };

            const url = `${tenantApiPath(layer.tenant_id, "layers/")}${layer.id}`;

            const response = await callAvosApi(`/${url}`, { method: "PATCH", data: payload });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate layer queries since it was updated
            queryClient.invalidateQueries({
                queryKey: ["layers", variables.layer.tenant_id],
            });
        },
        onError: (error, variables, context) => {
            if (context?.queryKey) {
                queryClient.setQueryData(context.queryKey, context.previous);
            }
            console.error("Failed to patch layer:", error);
        },
    });
}
